import type { EventRecord } from '@polkadot/types/interfaces'
import type { ApiPromise } from '@polkadot/api'

/**
 * Retrieves data about a block at the given block number.
 * @param api - The API interface for interacting with the blockchain.
 * @param blockNumber - The block number to retrieve data for.
 * @returns - A Promise that resolves with an object containing the following properties:
 *  - blockNumber: The block number of the retrieved block.
 *  - blockHash: The hex-encoded block hash of the retrieved block.
 *  - extrinsics: An array of extrinsics included in the retrieved block.
 *  - events: An array of events emitted by the retrieved block.
 */
export async function getBlockData(api: ApiPromise, blockNumber: number) {
  const blockHash = await api.rpc.chain.getBlockHash(blockNumber);

  const block = await api.rpc.chain.getBlock(blockHash);

  const apiAt = await api.at(blockHash);
  const events = await apiAt.query.system.events<EventRecord[]>()

  const extrinsics = block.block.extrinsics.map(({ method: { args, method, section } }) => (
    { section, method, args: args.map(i => i.toJSON()) }
  ));

  return {
    blockNumber,
    blockHash: blockHash.toHex(),
    extrinsics,
    events: events.map(({ event: { section, method, data } }) => ({ section, method, data: data.toJSON() })),
  }
}

